import { addDays, addMonths, format, startOfMonth, endOfMonth } from "date-fns";
import {
  Expense,
  ExpenseSection,
  SectionTotal,
  Income,
  ShoppingListItem,
} from "./types";
import { getTodayLocal, isTodayLocal, isTomorrowLocal } from "./dateUtils";
import * as incomeUtils from "./incomeUtils";
import { TaxProfile, getMonthlyTaxFromProfile } from "./taxCalculatorUtils";

// Timezone-aware date comparison utilities
const isTodayUTC = isTodayLocal;
const isTomorrowUTC = isTomorrowLocal;

const isSameMonthUTC = (
  date1: Date | null | undefined,
  date2: Date
): boolean => {
  if (!date1 || !(date1 instanceof Date)) return false;
  return (
    date1.getUTCFullYear() === date2.getUTCFullYear() &&
    date1.getUTCMonth() === date2.getUTCMonth()
  );
};

const createUTCDate = (year: number, month: number, day: number): Date => {
  return new Date(Date.UTC(year, month, day));
};

export const generateProjectedExpenses = (
  recurringExpenses: Expense[],
  monthOffset: number
): (Expense & { isProjected?: boolean })[] => {
  const now = new Date();
  const targetMonth = addMonths(now, monthOffset);
  const targetYear = targetMonth.getFullYear();
  const targetMonthNum = targetMonth.getMonth();
  const lastDayOfTargetMonth = new Date(
    Date.UTC(targetYear, targetMonthNum + 1, 0)
  ).getUTCDate();

  const projectedExpenses: (Expense & { isProjected?: boolean })[] = [];

  recurringExpenses
    .filter(
      (expense) =>
        expense.isRecurring &&
        // Don't project from already-generated recurring instances, otherwise
        // we can create duplicate/surprising projections.
        !expense.isRecurringInstance &&
        !expense.isPaid &&
        !expense.id.startsWith("projected-") &&
        !(expense as Expense & { isProjected?: boolean }).isProjected
    )
    .forEach((expense) => {
      const originalDate = expense.dueDate;
      const dayOfMonth = originalDate.getUTCDate();
      const pattern = (expense.recurrencePattern || "monthly").toLowerCase();

      if (pattern === "weekly") {
        const originalDayOfWeek = originalDate.getUTCDay();
        const firstOfMonth = new Date(Date.UTC(targetYear, targetMonthNum, 1));
        const firstDayOfWeek = firstOfMonth.getUTCDay();

        let daysUntilFirst = originalDayOfWeek - firstDayOfWeek;
        if (daysUntilFirst < 0) daysUntilFirst += 7;
        let currentDay = 1 + daysUntilFirst;

        let weekIndex = 0;
        while (currentDay <= lastDayOfTargetMonth) {
          const projectedDate = new Date(
            Date.UTC(targetYear, targetMonthNum, currentDay)
          );
          projectedExpenses.push({
            ...expense,
            id: `projected-${expense.id}-${monthOffset}-w${weekIndex}`,
            dueDate: projectedDate,
            isPaid: false,
            isProjected: true,
            originalId:
              (expense as Expense & { originalId?: string }).originalId ||
              expense.id,
          } as Expense & { isProjected?: boolean });
          currentDay += 7;
          weekIndex++;
        }
      } else if (pattern === "bi-weekly" || pattern === "biweekly") {
        const originalDayOfWeek = originalDate.getUTCDay();
        const firstOfMonth = new Date(Date.UTC(targetYear, targetMonthNum, 1));
        const firstDayOfWeek = firstOfMonth.getUTCDay();

        let daysUntilFirst = originalDayOfWeek - firstDayOfWeek;
        if (daysUntilFirst < 0) daysUntilFirst += 7;
        let currentDay = 1 + daysUntilFirst;

        let biweekIndex = 0;
        while (currentDay <= lastDayOfTargetMonth) {
          const projectedDate = new Date(
            Date.UTC(targetYear, targetMonthNum, currentDay)
          );
          projectedExpenses.push({
            ...expense,
            id: `projected-${expense.id}-${monthOffset}-bw${biweekIndex}`,
            dueDate: projectedDate,
            isPaid: false,
            isProjected: true,
            originalId:
              (expense as Expense & { originalId?: string }).originalId ||
              expense.id,
          } as Expense & { isProjected?: boolean });
          currentDay += 14;
          biweekIndex++;
        }
      } else {
        const monthDiff =
          (targetYear - originalDate.getUTCFullYear()) * 12 +
          (targetMonthNum - originalDate.getUTCMonth());
        const isQuarterly = pattern === "quarterly";
        const isSemiAnnual =
          pattern === "semiannual" ||
          pattern === "semi-annual" ||
          pattern === "every_6_months";
        const isAnnual =
          pattern === "annually" ||
          pattern === "annual" ||
          pattern === "yearly";
        const cadenceMonths = isQuarterly
          ? 3
          : isSemiAnnual
            ? 6
            : isAnnual
              ? 12
              : 1;
        if (monthDiff < 0 || monthDiff % cadenceMonths !== 0) {
          return;
        }

        const adjustedDay = Math.min(dayOfMonth, lastDayOfTargetMonth);
        const projectedDate = new Date(
          Date.UTC(targetYear, targetMonthNum, adjustedDay)
        );

        projectedExpenses.push({
          ...expense,
          id: `projected-${expense.id}-${monthOffset}`,
          dueDate: projectedDate,
          isPaid: false,
          isProjected: true,
          originalId:
            (expense as Expense & { originalId?: string }).originalId ||
            expense.id,
        } as Expense & { isProjected?: boolean });
      }
    });

  return projectedExpenses;
};

// Compare two dates by their UTC date components only (ignoring time)
// Returns: -1 if date1 < date2, 0 if equal, 1 if date1 > date2
const compareDatesUTC = (date1: Date, date2: Date): number => {
  const y1 = date1.getUTCFullYear();
  const m1 = date1.getUTCMonth();
  const d1 = date1.getUTCDate();

  const y2 = date2.getUTCFullYear();
  const m2 = date2.getUTCMonth();
  const d2 = date2.getUTCDate();

  if (y1 < y2) return -1;
  if (y1 > y2) return 1;
  if (m1 < m2) return -1;
  if (m1 > m2) return 1;
  if (d1 < d2) return -1;
  if (d1 > d2) return 1;
  return 0;
};

// Check if date1 is strictly before date2 (by UTC date only)
const isBeforeDateUTC = (date1: Date, date2: Date): boolean => {
  return compareDatesUTC(date1, date2) < 0;
};

// Check if date1 is before or equal to date2 (by UTC date only)
const isBeforeOrEqualDateUTC = (date1: Date, date2: Date): boolean => {
  return compareDatesUTC(date1, date2) <= 0;
};

// Check if date1 is strictly after date2 (by UTC date only)
const isAfterDateUTC = (date1: Date, date2: Date): boolean => {
  return compareDatesUTC(date1, date2) > 0;
};

// Check if date1 is after or equal to date2 (by UTC date only)
const isAfterOrEqualDateUTC = (date1: Date, date2: Date): boolean => {
  return compareDatesUTC(date1, date2) >= 0;
};

export const categorizeExpenses = (
  expenses: Expense[]
): Record<ExpenseSection, Expense[]> => {
  // Create a UTC date representing today at midnight, based on user's timezone
  const todayUTC = getTodayLocal();
  const tomorrowUTC = addDays(todayUTC, 1);
  const endOfWeek = addDays(todayUTC, 7);
  const endOfNext10Days = addDays(todayUTC, 10);
  const nextMonth = addMonths(todayUTC, 1);
  const endOfNextMonth = endOfMonth(nextMonth);
  const expected30Days = addDays(todayUTC, 30);

  console.log("[categorizeExpenses] Reference dates:", {
    today: todayUTC.toISOString(),
    todayUTCParts: {
      year: todayUTC.getUTCFullYear(),
      month: todayUTC.getUTCMonth(),
      day: todayUTC.getUTCDate(),
    },
  });

  return expenses.reduce(
    (acc, expense) => {
      const { dueDate } = expense;

      if (expense.isSkipped) {
        console.log("[categorizeExpenses] Skipping flagged expense:", {
          name: expense.name,
          id: expense.id,
          dueDate: dueDate instanceof Date ? dueDate.toISOString() : dueDate,
        });
        return acc;
      }

      // Paid items should not appear in time-based "due" buckets.
      // Keep them in the "Received" section only.
      if (expense.isPaid) {
        acc.received.push(expense);
        return acc;
      }

      // Skip if no valid date
      if (!dueDate || !(dueDate instanceof Date)) return acc;

      // Debug: log each expense's date comparison
      const isBeforeToday = isBeforeDateUTC(dueDate, todayUTC);
      const isOverdue = isBeforeToday && !expense.isPaid;
      console.log("[categorizeExpenses] Expense:", {
        name: expense.name,
        dueDate: dueDate.toISOString(),
        dueDateUTCParts: {
          year: dueDate.getUTCFullYear(),
          month: dueDate.getUTCMonth(),
          day: dueDate.getUTCDate(),
        },
        isPaid: expense.isPaid,
        isBeforeToday,
        willBeOverdue: isOverdue,
      });

      // Overdue expenses - only unpaid expenses that are strictly before today
      if (isOverdue) {
        acc.overdue.push(expense);
        // Also add to expected section if within 30 days
        if (isAfterOrEqualDateUTC(dueDate, addDays(todayUTC, -30))) {
          acc.expected.push(expense);
        }
        return acc;
      }
      // Due today
      if (isTodayUTC(dueDate)) {
        acc.today.push(expense);
      }
      // Due tomorrow
      else if (isTomorrowUTC(dueDate)) {
        acc.tomorrow.push(expense);
      }
      // Due this week (excluding tomorrow)
      else if (
        isAfterOrEqualDateUTC(dueDate, addDays(tomorrowUTC, 1)) &&
        isBeforeOrEqualDateUTC(dueDate, endOfWeek)
      ) {
        acc.thisWeek.push(expense);
      }
      // Within 10 Days: days 8-10 from today (exclusive of Today/Tomorrow/Within 7 Days).
      else if (
        isAfterOrEqualDateUTC(dueDate, addDays(endOfWeek, 1)) &&
        isBeforeOrEqualDateUTC(dueDate, endOfNext10Days)
      ) {
        acc.next10Days.push(expense);
      }

      // Month-based buckets (inclusive, even if already in time buckets)
      const isCurrentMonth = isSameMonthUTC(dueDate, todayUTC);
      const isNextMonth = isSameMonthUTC(dueDate, nextMonth);

      if (isCurrentMonth) {
        acc.thisMonth.push(expense);
      }

      if (isNextMonth) {
        acc.nextMonth.push(expense);
      }

      if (isAfterDateUTC(dueDate, endOfNextMonth)) {
        acc.upcoming.push(expense);
      }

      if (isBeforeOrEqualDateUTC(dueDate, expected30Days)) {
        acc.expected.push(expense);
      }

      return acc;
    },
    {
      received: [] as Expense[],
      pendingDeposit: [] as Expense[],
      overdue: [] as Expense[],
      today: [] as Expense[],
      tomorrow: [] as Expense[],
      thisWeek: [] as Expense[],
      next10Days: [] as Expense[],
      thisMonth: [] as Expense[],
      nextMonth: [] as Expense[],
      expected: [] as Expense[],
      upcoming: [] as Expense[],
    }
  );
};

export const calculateSectionTotals = (
  expensesBySection: Record<ExpenseSection, Expense[]>,
  debts?: any[],
  context?: ExpenseAmountContext
): SectionTotal[] => {
  return Object.entries(expensesBySection).map(([section, expenses]) => ({
    section: section as ExpenseSection,
    total: expenses
      .filter((expense) => !expense.isSkipped)
      .reduce(
        // Mirror ExpenseList.getEffectiveAmount so the section/card total
        // matches the sum of the rows shown in the table: compute the
        // effective amount WITH context (percent-income / tax-profile need
        // income + taxProfiles to resolve, otherwise they evaluate to 0),
        // then subtract recorded partial payments so we count the remaining
        // balance rather than the full amount.
        (sum, expense) => {
          const base = getEffectiveExpenseAmount(expense, debts, context);
          const remaining =
            !expense.isPaid && expense.paymentsTotal
              ? Math.max(0, base - Number(expense.paymentsTotal))
              : base;
          return sum + remaining;
        },
        0
      ),
  }));
};

export const formatCurrency = (amount: number): string => {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
  }).format(amount);
};

// Get the current month name (timezone-aware)
export const getCurrentMonthName = (): string => {
  const todayUTC = getTodayLocal();
  return format(todayUTC, "MMMM");
};

// Get the next month name (timezone-aware)
export const getNextMonthName = (): string => {
  const todayUTC = getTodayLocal();
  return format(addMonths(todayUTC, 1), "MMMM");
};

// Get all expenses for the current month (both paid and unpaid)
export const getCurrentMonthExpenses = (expenses: Expense[]): Expense[] => {
  const nowUTC = getTodayLocal();
  const firstDayOfMonth = startOfMonth(nowUTC);
  const lastDayOfMonth = endOfMonth(nowUTC);

  return expenses.filter((expense) => {
    if (!expense.dueDate || !(expense.dueDate instanceof Date)) {
      return false;
    }
    return (
      isAfterOrEqualDateUTC(expense.dueDate, firstDayOfMonth) &&
      isBeforeOrEqualDateUTC(expense.dueDate, lastDayOfMonth)
    );
  });
};

export const getCurrentMonthProjectedIncomeTotal = (
  incomeItems: Income[]
): number => {
  return incomeUtils.getCurrentMonthIncomeTotal(incomeItems);
};

export const getCurrentMonthActualIncomeTotal = (
  incomeItems: Income[]
): number => {
  const currentMonthIncome = incomeUtils.getCurrentMonthIncome(incomeItems);
  const actualIncome = currentMonthIncome.filter(
    (income) => income.receivedDate instanceof Date || income.isPaid
  );
  return actualIncome.reduce((total, income) => {
    const paidToDate = Number(income.paidToDate || 0);
    if (paidToDate > 0) return total + paidToDate;
    return total + incomeUtils.getInvoiceTotal(income);
  }, 0);
};

// Calculate the total of all expenses for the current month (excluding skipped)
export const getCurrentMonthExpensesTotal = (
  expenses: Expense[],
  debts?: any[]
): number => {
  const currentMonthExpenses = getCurrentMonthExpenses(expenses);
  return currentMonthExpenses
    .filter((expense) => !expense.isSkipped)
    .reduce((total, expense) => {
      const effectiveAmount = getEffectiveExpenseAmount(expense, debts);
      return total + effectiveAmount;
    }, 0);
};

// Get remaining (unpaid) expenses for the current month
export const getRemainingMonthExpenses = (expenses: Expense[]): Expense[] => {
  const currentMonthExpenses = getCurrentMonthExpenses(expenses);
  return currentMonthExpenses.filter((expense) => !expense.isPaid);
};

// Calculate the total of remaining (unpaid) expenses for the current month (excluding skipped)
export const getRemainingMonthExpensesTotal = (
  expenses: Expense[],
  debts?: any[]
): number => {
  const remainingExpenses = getRemainingMonthExpenses(expenses);
  return remainingExpenses
    .filter((expense) => !expense.isSkipped)
    .reduce((total, expense) => {
      const effectiveAmount = getEffectiveExpenseAmount(expense, debts);
      return total + effectiveAmount;
    }, 0);
};

export const getRemainingShoppingListTotal = (
  items: ShoppingListItem[] | null | undefined
): number => {
  if (!items || items.length === 0) return 0;
  return items.reduce((sum, item) => {
    if (item.isPaid || item.isSkipped) {
      return sum;
    }
    return sum + Number(item.price || 0);
  }, 0);
};

export type ExpenseAmountContext = {
  income?: Income[];
  taxProfiles?: TaxProfile[];
};

// Helper function to get effective expense amount (from linked debt if available)
export const getEffectiveExpenseAmount = (
  expense: Expense,
  debts?: any[],
  context?: ExpenseAmountContext
): number => {
  const calculationType = expense.calculationType || "fixed";
  if (calculationType === "percent_income") {
    const percent = Number(expense.percentOfIncome || 0);
    const basis = expense.incomeBasis || "projected";
    const incomeTotal = context?.income
      ? basis === "actual"
        ? getCurrentMonthActualIncomeTotal(context.income)
        : getCurrentMonthProjectedIncomeTotal(context.income)
      : 0;
    const computedAmount = (incomeTotal * percent) / 100;
    console.debug("[expenseUtils] Computed percent income amount", {
      expenseId: expense.id,
      expenseName: expense.name,
      basis,
      percent,
      incomeTotal,
      computedAmount,
    });
    return computedAmount;
  }

  if (calculationType === "tax_profile") {
    const profiles = context?.taxProfiles || [];
    const profile =
      profiles.find((p) => p.id === expense.taxProfileId) ||
      profiles.find((p) => p.name === expense.taxProfileName);
    const computedAmount = profile ? getMonthlyTaxFromProfile(profile) : 0;
    console.debug("[expenseUtils] Computed tax profile amount", {
      expenseId: expense.id,
      expenseName: expense.name,
      taxProfileId: expense.taxProfileId,
      taxProfileName: expense.taxProfileName,
      computedAmount,
    });
    return computedAmount;
  }

  if (
    expense.linkedDebtId &&
    expense.useLinkedDebtAmount !== false &&
    debts &&
    debts.length > 0
  ) {
    const linkedDebt = debts.find(
      (debt: any) => debt.id === expense.linkedDebtId
    );
    const debtPayment =
      linkedDebt?.maxMonthlyPayment ?? linkedDebt?.max_monthly_payment;
    if (debtPayment) {
      return Math.abs(Number(debtPayment));
    }
  }
  return Number(expense.amount || 0);
};

// Calculate suspension date from due date and period
export const calculateSuspensionDate = (
  dueDate: Date,
  periodValue: number,
  periodUnit: "days" | "months"
): Date => {
  // Dates in this app are stored as midnight UTC. date-fns `addDays/addMonths`
  // operate in the runtime's local timezone, which can shift the UTC calendar
  // date and cause off-by-one issues. Keep all date math in UTC instead.
  const addDaysUTC = (date: Date, days: number): Date => {
    return new Date(
      Date.UTC(
        date.getUTCFullYear(),
        date.getUTCMonth(),
        date.getUTCDate() + days
      )
    );
  };

  const addMonthsUTC = (date: Date, months: number): Date => {
    const year = date.getUTCFullYear();
    const month = date.getUTCMonth();
    const day = date.getUTCDate();

    const totalMonths = year * 12 + month + months;
    const targetYear = Math.floor(totalMonths / 12);
    const targetMonth = totalMonths - targetYear * 12; // 0..11

    const lastDayOfTargetMonth = new Date(
      Date.UTC(targetYear, targetMonth + 1, 0)
    ).getUTCDate();

    // Clamp day-of-month (e.g. Jan 31 + 1 month => Feb 28/29).
    const targetDay = Math.min(day, lastDayOfTargetMonth);
    return new Date(Date.UTC(targetYear, targetMonth, targetDay));
  };

  if (periodUnit === "months") return addMonthsUTC(dueDate, periodValue);
  return addDaysUTC(dueDate, periodValue);
};

const MS_PER_DAY = 1000 * 60 * 60 * 24;

// Get days until suspension relative to a supplied "today" (useful for tests and deterministic UI logic).
export const getDaysUntilSuspensionFrom = (
  todayUTC: Date,
  dueDate: Date | undefined,
  suspensionPeriodValue: number | undefined,
  suspensionPeriodUnit: "days" | "months" | undefined
): number | null => {
  if (
    !dueDate ||
    suspensionPeriodValue === undefined ||
    !suspensionPeriodUnit
  ) {
    return null;
  }

  const suspensionDate = calculateSuspensionDate(
    dueDate,
    suspensionPeriodValue,
    suspensionPeriodUnit
  );

  const todayDateOnlyUTC = createUTCDate(
    todayUTC.getUTCFullYear(),
    todayUTC.getUTCMonth(),
    todayUTC.getUTCDate()
  );

  const suspensionDateOnlyUTC = createUTCDate(
    suspensionDate.getUTCFullYear(),
    suspensionDate.getUTCMonth(),
    suspensionDate.getUTCDate()
  );

  const diffMs = suspensionDateOnlyUTC.getTime() - todayDateOnlyUTC.getTime();
  return Math.floor(diffMs / MS_PER_DAY);
};

// Get days until suspension (negative if past suspension date)
export const getDaysUntilSuspension = (
  dueDate: Date | undefined,
  suspensionPeriodValue: number | undefined,
  suspensionPeriodUnit: "days" | "months" | undefined
): number | null => {
  return getDaysUntilSuspensionFrom(
    getTodayLocal(),
    dueDate,
    suspensionPeriodValue,
    suspensionPeriodUnit
  );
};
